import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type LocaleType = 'en-US' | 'en-GB' | 'ru-RU' | 'uk-UA' ;

interface DateTimeContextType {
  locale: LocaleType;
  setLocale: (locale: LocaleType) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
  formatDate: (date: Date, options?: Intl.DateTimeFormatOptions) => string;
  formatTime: (date: Date, options?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (date: Date, options?: Intl.DateTimeFormatOptions) => string;
}

const DateTimeContext = createContext<DateTimeContextType | undefined>(undefined);

export const DateTimeProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<LocaleType>(() => {
    const saved = localStorage.getItem('dateLocale');
    return (saved as LocaleType) || 'en-US';
  });

  const [timezone, setTimezoneState] = useState<string>(() => {
    const saved = localStorage.getItem('timezone');
    return saved || Intl.DateTimeFormat().resolvedOptions().timeZone;
  });

  useEffect(() => {
    localStorage.setItem('dateLocale', locale);
  }, [locale]);

  useEffect(() => {
    localStorage.setItem('timezone', timezone);
  }, [timezone]);

  const setLocale = (newLocale: LocaleType) => {
    setLocaleState(newLocale);
  };

  const setTimezone = (newTimezone: string) => {
    setTimezoneState(newTimezone);
  };

  const formatDate = (date: Date, options?: Intl.DateTimeFormatOptions) => {
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      dateStyle: 'medium',
      ...options,
    }).format(date);
  };

  const formatTime = (date: Date, options?: Intl.DateTimeFormatOptions) => {
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeStyle: 'medium',
      ...options,
    }).format(date);
  };

  const formatDateTime = (date: Date, options?: Intl.DateTimeFormatOptions) => {
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      dateStyle: 'medium',
      timeStyle: 'short',
      ...options,
    }).format(date);
  };

  return (
    <DateTimeContext.Provider
      value={{
        locale,
        setLocale,
        timezone,
        setTimezone,
        formatDate,
        formatTime,
        formatDateTime,
      }}
    >
      {children}
    </DateTimeContext.Provider>
  );
};

export const useDateTime = () => {
  const context = useContext(DateTimeContext);
  if (!context) {
    throw new Error('useDateTime must be used within DateTimeProvider');
  }
  return context;
};